import { Btn, H6 } from "AbstractElements";
import { Add, Cancel, Update } from "Constant";
import InputNumber from "rc-input-number";
import { Fragment, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { Control, Controller, ControllerRenderProps } from "react-hook-form";
import { useParams } from "react-router";
import Select from "react-select";
import { Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import Dropzone from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import {
  IForm,
  IFormItem,
  IFormItemElement,
  IFormItemRow,
  IOption,
} from "./interface";
import { formatNumber, unformatNumber } from "utils/format";

const TheForm = (props: IForm) => {
  const { id } = useParams();
  const {
    dir = "verical",
    sections,
    items,
    onEdit,
    onSubmit,
    onCancel,
    control,
    textOnly,
    borderY,
    formState: { errors },
  } = props;

  return (
    <Form
      className={`theme-form ${
        borderY ? "border !border-y-0 px-4 border-gray-50" : ""
      }`}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
    >
      {sections && (
        <Fragment>
          {sections.map(
            ({ items, title, type, name, options, className }, rowIndex) => (
              <Fragment key={rowIndex}>
                {type && name ? (
                  <div className="flex gap-4 items-center">
                    <H6>{title}</H6>
                    {items && !isRadioGroup(items) && (
                      <FormItem
                        className={className}
                        textOnly={textOnly}
                        name={name}
                        type={type}
                        control={control}
                        error={!!errors.name}
                        options={options}
                      />
                    )}
                  </div>
                ) : (
                  <H6>{title}</H6>
                )}
                {items &&
                  (isRadioGroup(items) ? (
                    <RadioGroup
                      textOnly={textOnly}
                      controllerProps={{ control, name }}
                      items={items}
                    />
                  ) : (
                    <div
                      className="grid grid-flow-col-dense grid-cols-2 grid-rows-[repeat(var(--rowCol),minmax(0,_max-content))] gap-x-16"
                      style={{
                        /* @ts-ignore */
                        "--rowCol": Math.round(items.length / 2),
                      }}
                    >
                      <FormItemRow
                        className={className}
                        textOnly={textOnly}
                        dir={dir}
                        items={items}
                        control={control}
                        errors={errors}
                      />
                    </div>
                  ))}
                <hr className="mt-4 mb-4" />
              </Fragment>
            )
          )}
        </Fragment>
      )}
      {items && (
        <div className="grid grid-cols-2 gap-x-16">
          <FormItemRow
            textOnly={textOnly}
            dir={dir}
            items={items}
            control={control}
            errors={errors}
          />
        </div>
      )}
      {(onCancel || onSubmit) && (
        <Row>
          <Col>
            <div className="text-end">
              {onCancel && (
                <Btn
                  attrBtn={{
                    color: "danger",
                    className: "me-3",
                    onClick: onCancel,
                  }}
                >
                  {Cancel}
                </Btn>
              )}
              {onEdit && (
                <Btn
                  attrBtn={{
                    color: "success",
                    className: "me-3",
                    onClick: onEdit,
                  }}
                >
                  {Update}
                </Btn>
              )}
              {onSubmit && (
                <Btn attrBtn={{ color: "success" }}>{id ? Update : Add}</Btn>
              )}
            </div>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export const FormItemRow = (props: IFormItemRow) => {
  const { items, control, errors, dir, textOnly } = props;
  return (
    items && (
      <Fragment>
        {items.map((item, rowIndex) =>
          item.isHidden ? (
            <Fragment key={rowIndex}></Fragment>
          ) : (
            <div
              className={`grid gap-x-16 gap-y-4 ${item.className}`}
              key={rowIndex}
            >
              <Fragment>
                <FormItem
                  textOnly={textOnly}
                  dir={dir}
                  key={item.name}
                  {...item}
                  control={control}
                  error={!!errors[item.name]}
                  errorMsg={errors[item.name]?.message ?? ""}
                />
              </Fragment>
            </div>
          )
        )}
      </Fragment>
    )
  );
};

export const FormItem = (
  props: IFormItem & { control: Control; error: boolean }
) => {
  const { dir, label, isHidden, ...rest } = props;
  const { rules, error, errorMsg } = rest;
  return isHidden ? (
    <></>
  ) : (
    <div>
      {dir === "hozirontal" ? (
        <FormGroup row>
          {rest.textOnly ? (
            <H6>{label}</H6>
          ) : (
            rest.type !== "checkbox" &&
            label && (
              <Label>
                {label}{" "}
                {rules?.required && <span className="text-red-500">*</span>}
              </Label>
            )
          )}

          <Col className="col-form-label">
            <FormItemElement label={label} {...rest} />
          </Col>
          {error && (
            <span className="text-red-500">{errorMsg?.toString()}</span>
          )}
        </FormGroup>
      ) : (
        <FormGroup row>
          {rest.textOnly ? (
            <H6>{label}</H6>
          ) : (
            rest.type !== "checkbox" &&
            label && (
              <Label>
                {label}{" "}
                {rules?.required && <span className="text-red-500">*</span>}
              </Label>
            )
          )}
          <FormItemElement label={label} {...rest} />
          {error && (
            <span className="text-red-500">{errorMsg?.toString()}</span>
          )}
        </FormGroup>
      )}
    </div>
  );
};

const FormItemElement = (props: IFormItemElement) => {
  const {
    type,
    options,
    error,
    errorMsg,
    textOnly,
    trueValue = true,
    falseValue = false,
    Element,
    label,
    disabled,
    minDate,
    maxDate,
    onChange,
    ...rest
  } = props;

  if (textOnly) {
    return (
      <Controller
        {...rest}
        render={({ field }) => <span>{field.value}</span>}
      />
    );
  }

  if (type === "input") {
    return (
      <div>
        <Controller
          {...rest}
          disabled={disabled}
          render={({ field }) => <Input {...field} />}
        />
      </div>
    );
  }

  if (type === "text") {
    return (
      <div>
        <Controller
          {...rest}
          render={({ field }) => (
            <Input
              type="textarea"
              {...field}
              disabled={disabled}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e);
              }}
            />
          )}
        />
      </div>
    );
  }

  if (type === "number") {
    return (
      <div>
        <Controller
          {...rest}
          render={({ field }) => (
            <Input
              type="text"
              {...field}
              className="form-control digits"
              disabled={disabled}
              value={typeof field.value === 'number' ? formatNumber(field.value) : field.value}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d.-]/g, '');
                field.onChange(value ? Number(value) : '');
              }}
              onBlur={(e) => {
                const value = unformatNumber(e.target.value);
                field.onChange(value ? Number(value) : '');
              }}
            />
          )}
        />
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <Controller
        {...rest}
        render={({ field }) => (
          <div className="checkbox checkbox-primary checkbox--transform-y ml-4">
            <Input
              className="hidden"
              checked={field.value === trueValue}
              defaultValue={falseValue as any}
              id={`id-${rest.name}`}
              type="checkbox"
              {...field}
              onChange={(e) => {
                field.onChange(e.target.checked ? trueValue : falseValue);
                onChange?.(e.target.checked ? trueValue : falseValue);
              }}
            />
            <Label for={`id-${rest.name}`}>{label}</Label>
          </div>
        )}
      />
    );
  }

  if (type === "checkbox-group") {
    return (
      <div className="flex gap-4 checkbox--transform-y">
        {options &&
          options.map(({ value, text, name }) => (
            <div key={name ?? "" + value} className="checkbox checkbox-primary">
              <Controller
                {...rest}
                name={`${rest.name}-${name}`}
                render={({ field }) => (
                  <Fragment>
                    <Input
                      id={`id-${name}`}
                      type="checkbox"
                      {...field}
                      value={value}
                    />
                    <Label for={`id-${name}`}>{text}</Label>
                  </Fragment>
                )}
              />
            </div>
          ))}
      </div>
    );
  }

  if (type === "radio") {
    return (
      <div className="flex gap-4">
        <Controller
          {...rest}
          render={({ field }) => (
            <RadioElement field={field} options={options} />
          )}
        />
      </div>
    );
  }

  if (type === "radio-text") {
    return (
      <Controller
        {...rest}
        render={({ field }) => <RadioText field={field} options={options} />}
      />
    );
  }

  if (type === "date") {
    return (
      <Controller
        {...rest}
        render={({ field }) => (
          <DatePicker
            style={{ width: "100%" }}
            wrapperClassName="w-full"
            className="form-control digits"
            selected={field.value}
            onChange={field.onChange}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            showMonthDropdown
            showYearDropdown
            dateFormat="dd/MM/yyyy"
          />
        )}
      />
    );
  }

  if (type === "time") {
    return (
      <Controller
        {...rest}
        render={({ field }) => (
          <DatePicker
            style={{ width: "100%" }}
            wrapperClassName="w-full"
            className="form-control digits"
            selected={field.value}
            onChange={field.onChange}
            disabled={disabled}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Thời gian"
            dateFormat="h:mm"
          />
        )}
      />
    );
  }

  if (type === "year") {
    return (
      <Controller
        {...rest}
        render={({ field }) => (
          <DatePicker
            showYearPicker
            dateFormat="yyyy"
            style={{ width: "100%" }}
            wrapperClassName="w-full"
            className="form-control digits"
            selected={
              Number.isInteger(field.value)
                ? new Date(field.value, 1)
                : field.value
            }
            onChange={(value) => field.onChange(value?.getFullYear())}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (type === "select" || type === "dropdownlist") {
    return (
      <Controller
        {...rest}
        render={({ field }) => (
          <Select
            {...field}
            value={options?.find((x) => x.value === field.value) || null}
            getOptionValue={(option: IOption) => option.value?.toString()}
            getOptionLabel={(option: IOption) => option.text}
            className="w-full"
            placeholder="Chọn"
            options={options}
            onChange={(option: IOption) => {
              field.onChange(option?.value);
              onChange?.(option?.value);
            }}
            isDisabled={disabled}
            isClearable
          />
        )}
      />
    );
  }

  if (type === "custom") {
    return (
      <Controller {...rest} render={({ field }) => <Element {...field} />} />
    );
  }

  if (type === "upload") {
    return (
      <Controller
        {...rest}
        render={({ field }) => (
          /* @ts-ignore */
          <Dropzone
            onChangeStatus={({ meta, file }, status) => {
              if (status === "done") {
                field.onChange(file);
              }
            }}
            onSubmit={(files, allFiles) => {
              allFiles.forEach((f) => f.remove());
            }}
            accept="image/*"
            maxFiles={1}
            multiple={false}
            inputContent="Kéo thả hoặc click để chọn ảnh"
            styles={{
              dropzone: {
                minHeight: 100,
                maxHeight: 200,
                border: "1px dashed #d9d9d9",
                borderRadius: "4px",
              },
              inputLabel: {
                color: "#666",
              },
            }}
            {...(textOnly
              ? {
                  disabled: true,
                  value: field.value ? [field.value] : [],
                }
              : {})}
          />
        )}
      />
    );
  }

  return <Fragment></Fragment>;
};

interface IRadioText {
  options?: IOption[];
  field: ControllerRenderProps<any, any>;
  textOnly?: boolean;
}

const RadioGroup = ({
  items,
  controllerProps,
  textOnly,
}: {
  items: IFormItem[];
  controllerProps: any;
  textOnly?: boolean;
}) => {
  return (
    <div className="grid grid-rows-[repeat(2,minmax(0,_max-content))] grid-cols-2 gap-4 grid-flow-col-dense auto-rows-auto">
      <Controller
        {...controllerProps}
        render={({ field }) =>
          items.map((item) =>
            item.type === "radio-text" ? (
              <RadioText
                textOnly={textOnly}
                field={field}
                options={item.options}
              />
            ) : (
              <RadioElement
                textOnly={textOnly}
                field={field}
                options={item.options}
              />
            )
          )
        }
      />
    </div>
  );
};

const RadioElement = ({
  field,
  options,
  textOnly,
}: {
  options?: IOption[];
  field: any;
  textOnly?: boolean;
}) => (
  <Fragment>
    {options &&
      options.map(({ value, text }) =>
        textOnly ? (
          value === field.value ? (
            <span>{text}</span>
          ) : (
            <></>
          )
        ) : (
          <div key={value} className="radio radio-primary ml-5">
            <Input
              id={`id-${value}-${field.name}`}
              type="radio"
              {...field}
              value={value}
              checked={value == field.value}
            />
            <Label for={`id-${value}-${field.name}`}>{text}</Label>
          </div>
        )
      )}
  </Fragment>
);
const RadioText = ({ options, field, textOnly }: IRadioText) => {
  const { value, name, onChange } = field;
  const [inputValue, setInputValue] = useState("");

  const checked = useMemo(() => value?.includes(name), [name, value]);
  const combinedValue = useMemo(
    () => `${name}@${inputValue}`,
    [name, inputValue]
  );

  useEffect(() => {
    onChange(combinedValue);
  }, [combinedValue]);

  useEffect(() => {
    setInputValue(value?.split("@")[1] ?? "");
  }, [value]);

  return textOnly ? (
    <>{inputValue}</>
  ) : (
    <div>
      {options &&
        options.map(({ value, text }) => (
          <div key={value} className="radio radio-primary ml-5">
            <Input
              id={`id-${value}`}
              type="radio"
              {...field}
              value={name}
              checked={checked}
              onChange={() => onChange(combinedValue)}
            />
            <Label for={`id-${value}`}>{text}</Label>
          </div>
        ))}
      <Input
        className="mt-2"
        type="textarea"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />
    </div>
  );
};

const isRadioGroup = (items: IFormItem[]) =>
  items.some((item) => item.type === "radio");

export default TheForm;
